import React, { useState } from 'react';
import {
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword,
} from 'firebase/auth';

const FUNCTIONS_BASE_URL = process.env.REACT_APP_FUNCTIONS_BASE_URL;

// ── Icons ──────────────────────────────────────────────────────────────────

const PwIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round"
    style={{ width: 15, height: 15, color: '#bbb', flexShrink: 0 }}>
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
  </svg>
);

const EyeIcon = ({ visible }) => visible ? (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round"
    style={{ width: 16, height: 16 }}>
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
) : (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round"
    style={{ width: 16, height: 16 }}>
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
    <line x1="1" y1="1" x2="23" y2="23"/>
  </svg>
);

const ErrorIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round"
    style={{ width: 15, height: 15, flexShrink: 0 }}>
    <circle cx="12" cy="12" r="10"/>
    <line x1="12" y1="8" x2="12" y2="12"/>
    <line x1="12" y1="16" x2="12.01" y2="16"/>
  </svg>
);

const CheckCircleIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round"
    style={{ width: 40, height: 40, stroke: 'var(--orange)' }}>
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
    <polyline points="22 4 12 14.01 9 11.01"/>
  </svg>
);

const FRIENDLY_ERRORS = {
  'auth/wrong-password': 'Your current password is incorrect.',
  'auth/invalid-credential': 'Your current password is incorrect.',
  'auth/weak-password': 'Your new password is too weak. Please choose at least 8 characters.',
  'auth/too-many-requests': 'Too many attempts. Please wait a few minutes and try again.',
  'auth/requires-recent-login': 'Your session has expired. Please sign out and sign back in, then try again.',
};

// ── PasswordField sub-component ────────────────────────────────────────────

function PasswordField({ label, value, onChange, show, onToggle, placeholder, autoComplete }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{
        display: 'block', fontSize: 11, fontWeight: 600,
        color: 'var(--muted)', letterSpacing: '.6px', textTransform: 'uppercase',
        marginBottom: 6,
      }}>
        {label}
      </label>
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
        <span style={{ position: 'absolute', left: 12, display: 'flex', pointerEvents: 'none' }}>
          <PwIcon />
        </span>
        <input
          type={show ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          autoComplete={autoComplete}
          required
          style={{
            width: '100%', fontFamily: 'var(--sans)', fontSize: 14, color: 'var(--black)',
            padding: '10px 44px 10px 38px',
            border: '1.5px solid var(--line)', borderRadius: 3,
            background: 'var(--white)', outline: 'none',
            transition: 'border-color .15s',
          }}
          onFocus={(e) => { e.target.style.borderColor = 'var(--orange)'; }}
          onBlur={(e) => { e.target.style.borderColor = 'var(--line)'; }}
        />
        <button
          type="button"
          onClick={onToggle}
          style={{
            position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
            background: 'none', border: 'none', cursor: 'pointer', padding: 0,
            color: 'var(--muted)', display: 'flex', alignItems: 'center',
          }}
          aria-label={show ? 'Hide password' : 'Show password'}
        >
          <EyeIcon visible={show} />
        </button>
      </div>
    </div>
  );
}

// ── ChangePassword ──────────────────────────────────────────────────────────

/**
 * ChangePassword
 *
 * Props:
 *   user             — Firebase Auth user object (required)
 *   forced           — true when the user must set a password before continuing (first-login flow)
 *   onPasswordChanged — called after a successful password change
 *   onCancel         — called when the user dismisses the form (non-forced only)
 *
 * When forced=true the component renders a full-page centred card (matching the Login page).
 * When forced=false it renders just the form content, suitable for embedding in a Modal or Tab.
 */
function ChangePassword({ user, forced = false, onPasswordChanged, onCancel, onForgotPassword }) {
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const canSubmit = !!(currentPw && newPw && confirmPw && !loading);

  const reset = () => {
    setCurrentPw('');
    setNewPw('');
    setConfirmPw('');
    setError(null);
    setSuccess(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    if (newPw.length < 8) {
      setError('New password must be at least 8 characters.');
      return;
    }
    if (newPw !== confirmPw) {
      setError('New passwords do not match.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const credential = EmailAuthProvider.credential(user.email, currentPw);
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, newPw);

      if (forced && FUNCTIONS_BASE_URL) {
        try {
          const token = await user.getIdToken();
          await fetch(`${FUNCTIONS_BASE_URL}/clearForcePasswordChange`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
          });
        } catch (claimErr) {
          // Non-fatal — user has already changed their password
          console.warn('Could not clear forcePasswordChange claim:', claimErr);
        }
        // Refresh the token so subsequent getIdTokenResult() returns updated claims
        await user.getIdToken(true);
        onPasswordChanged && onPasswordChanged();
        return;
      }

      setSuccess(true);
      onPasswordChanged && onPasswordChanged();
    } catch (err) {
      const isWrongPassword = err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential';
      if (isWrongPassword && onForgotPassword) {
        setError(
          <>
            Your current password is incorrect.{' '}
            <button
              type="button"
              onClick={onForgotPassword}
              style={{
                background: 'none', border: 'none', padding: 0,
                cursor: 'pointer', color: 'inherit', textDecoration: 'underline',
                fontFamily: 'var(--sans)', fontSize: 'inherit',
              }}
            >
              Forgot your password?
            </button>
          </>
        );
      } else {
        setError(FRIENDLY_ERRORS[err.code] || 'Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // ── Success state ─────────────────────────────────────────────────────────

  if (success) {
    return (
      <div style={{ textAlign: 'center', padding: '16px 0' }}>
        <div style={{
          width: 64, height: 64, background: 'var(--almond)', borderRadius: '50%',
          margin: '0 auto 16px', display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <CheckCircleIcon />
        </div>
        <h3 style={{
          fontFamily: 'var(--serif)', fontSize: 18, fontWeight: 600,
          color: 'var(--black)', marginBottom: 8, marginTop: 0,
        }}>
          Password updated
        </h3>
        <p style={{ fontSize: 14, color: 'var(--mid)', marginBottom: 20, lineHeight: 1.6 }}>
          Your password has been changed successfully.
        </p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
          <button
            onClick={reset}
            style={{
              padding: '10px 24px',
              fontFamily: 'var(--sans)', fontSize: 13, fontWeight: 500,
              background: 'var(--white)', border: '1.5px solid var(--black)',
              borderRadius: 3, color: 'var(--black)', cursor: 'pointer',
              transition: 'background .12s',
            }}
            onMouseEnter={(e) => { e.target.style.background = 'var(--almond)'; }}
            onMouseLeave={(e) => { e.target.style.background = 'var(--white)'; }}
          >
            Change again
          </button>
          {onCancel && (
            <button
              onClick={onCancel}
              style={{
                padding: '10px 24px',
                fontFamily: 'var(--sans)', fontSize: 13, fontWeight: 600,
                background: 'var(--orange)', color: 'var(--white)',
                border: 'none', borderRadius: 3, cursor: 'pointer',
                transition: 'background .15s',
              }}
              onMouseEnter={(e) => { e.target.style.background = 'var(--orange-dark)'; }}
              onMouseLeave={(e) => { e.target.style.background = 'var(--orange)'; }}
            >
              Done
            </button>
          )}
        </div>
      </div>
    );
  }

  // ── Form ──────────────────────────────────────────────────────────────────

  const form = (
    <div>
      {forced && (
        <>
          <h2 style={{
            fontFamily: 'var(--serif)', fontSize: 22, fontWeight: 600,
            color: 'var(--black)', marginBottom: 8, marginTop: 0,
          }}>
            Set your password
          </h2>
          <p style={{ fontSize: 14, color: 'var(--mid)', lineHeight: 1.6, marginBottom: 24 }}>
            For security, please set your own password before continuing. Enter the temporary password
            from your onboarding email, then choose a new one.
          </p>
        </>
      )}

      {error && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          background: 'var(--err-bg)', borderRadius: 3,
          padding: '10px 12px', fontSize: 13, color: 'var(--err-txt)',
          marginBottom: 16,
        }}>
          <ErrorIcon />
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} noValidate>
        <PasswordField
          label="Current password"
          value={currentPw}
          onChange={setCurrentPw}
          show={showCurrent}
          onToggle={() => setShowCurrent((v) => !v)}
          placeholder="Your current password"
          autoComplete="current-password"
        />
        <PasswordField
          label="New password"
          value={newPw}
          onChange={setNewPw}
          show={showNew}
          onToggle={() => setShowNew((v) => !v)}
          placeholder="Min. 8 characters"
          autoComplete="new-password"
        />
        <div style={{ marginBottom: 24 }}>
          <PasswordField
            label="Confirm new password"
            value={confirmPw}
            onChange={setConfirmPw}
            show={showConfirm}
            onToggle={() => setShowConfirm((v) => !v)}
            placeholder="Repeat new password"
            autoComplete="new-password"
          />
        </div>

        <button
          type="submit"
          disabled={!canSubmit}
          style={{
            width: '100%', padding: 13,
            fontFamily: 'var(--sans)', fontSize: 14, fontWeight: 600,
            background: canSubmit ? 'var(--orange)' : '#f0c4a0',
            color: 'var(--white)',
            border: 'none', borderRadius: 3,
            cursor: canSubmit ? 'pointer' : 'default',
            letterSpacing: '.2px', transition: 'background .15s',
          }}
          onMouseEnter={(e) => { if (canSubmit) e.target.style.background = 'var(--orange-dark)'; }}
          onMouseLeave={(e) => { if (canSubmit) e.target.style.background = canSubmit ? 'var(--orange)' : '#f0c4a0'; }}
        >
          {loading ? 'Updating…' : forced ? 'Set password' : 'Update password'}
        </button>

        {!forced && onCancel && (
          <button
            type="button"
            onClick={onCancel}
            style={{
              width: '100%', marginTop: 10, padding: 12,
              fontFamily: 'var(--sans)', fontSize: 14, fontWeight: 500,
              background: 'var(--white)', border: '1.5px solid var(--black)',
              borderRadius: 3, color: 'var(--black)', cursor: 'pointer',
              transition: 'background .12s',
            }}
            onMouseEnter={(e) => { e.target.style.background = 'var(--almond)'; }}
            onMouseLeave={(e) => { e.target.style.background = 'var(--white)'; }}
          >
            Cancel
          </button>
        )}
      </form>
    </div>
  );

  // ── Full-page wrapper (forced first-login flow) ────────────────────────────

  if (forced) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--surface)',
        padding: '20px',
      }}>
        <div style={{
          background: 'var(--white)',
          width: '100%',
          maxWidth: 420,
          borderRadius: 6,
          overflow: 'hidden',
          boxShadow: '0 8px 32px rgba(33,33,33,.18)',
          animation: 'fadeUp .35s var(--ease) both',
        }}>
          <div style={{ padding: '44px 48px 40px' }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 28 }}>
              <img
                src="/assets/default-logo.svg"
                alt="TalentMapper"
                style={{ height: 26, width: 'auto', display: 'block' }}
              />
            </div>
            {form}
          </div>
        </div>
      </div>
    );
  }

  return form;
}

export default ChangePassword;
